import { ApiError } from "@procurement-agents/scope-api";
import { NotHeard, PROBLEM_TEXT, useRecorder, type RecorderProblem } from "@procurement-agents/voice";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useOptionalScopeApi } from "./api";
import { useHandsFree } from "./handsFree";

/** CHANGE_voice_level2. `enabled` is the tenant's switch (GET /tenant); `runId` attributes speech to the request. */
export interface VoiceSettings { enabled: boolean; runId?: string | null }
const VoiceContext = createContext<VoiceSettings>({ enabled: false });
export const VoiceProvider = VoiceContext.Provider;

/** Inside a run's route: speech made there is attributed to that run. */
export function RunVoice({ children }: { children: ReactNode }) {
  const outer = useContext(VoiceContext);
  const { id } = useParams();
  return <VoiceProvider value={{ ...outer, runId: id ?? null }}>{children}</VoiceProvider>;
}

export const AUDIO_NOT_KEPT = "Audio isn't kept — only the words.";

/** How often, at most, to warm the speech path: the API keeps its connection for two minutes. */
const WARM_EVERY_MS = 60_000;
let lastWarm = 0;

/** Ask the API to open its connection to the speech provider now, before anyone is waiting on it. */
export function warmSpeech(api: { warmSpeech(): Promise<void> } | undefined | null): void {
  if (!api || Date.now() - lastWarm < WARM_EVERY_MS) return;
  lastWarm = Date.now();
  void api.warmSpeech();
}
/**
 * Dictation, and the hooks the composer uses (CHANGE_voice_level2, reshaped by level 3 §14). Speaking the agent's
 * questions belongs to the Voice toggle, which is the hands-free conversation (`handsFree.tsx`); the mic is here, and
 * works whether Voice is on or not.
 */
export function useIntakeVoice() {
  const { enabled, runId = null } = useContext(VoiceContext);
  const api = useOptionalScopeApi()?.api;
  const hf = useHandsFree();
  const available = enabled && !!api;
  const transcribe = useCallback(async (audio: Blob, durationMs: number) => {
    if (!api) throw new Error("no api");
    try {
      return (await api.transcribe(audio, { runId, durationMs })).text;
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) throw new NotHeard(e.detail);   // words came back, but not an answer
      throw e;
    }
  }, [api, runId]);
  const yieldTurn = hf.yieldTurn;
  /** Typing or dictating takes the turn: Voice stops talking and listening until the next question. */
  const interrupt = useCallback(() => yieldTurn(), [yieldTurn]);
  return { available, transcribe, interrupt, warm: () => warmSpeech(api) };
}
export type IntakeVoice = ReturnType<typeof useIntakeVoice>;

const MIC_HINTED_KEY = "workspace.voice.mic_hinted";

/** Shown under the mic the first time this device dictates: what happens to the recording. */
function useFirstUse(): [boolean, () => void, () => void] {
  const [show, setShow] = useState(false);
  const mark = useCallback(() => {
    try { if (localStorage.getItem(MIC_HINTED_KEY)) return; localStorage.setItem(MIC_HINTED_KEY, "1"); } catch { return; }
    setShow(true);
  }, []);
  return [show, mark, () => setShow(false)];
}

/** What the mic has to say: a problem (red) or the first-use note. */
export interface MicMessageT { text: string; tone: "problem" | "note" }
/** How long a message stays before it clears itself. */
const MESSAGE_MS = 15_000;

/** Shown by the answer box, in the flow of the page on a line of its own: never over anything else. */
export function MicMessage({ message, onDismiss }: { message: MicMessageT | null; onDismiss: () => void }) {
  if (!message) return null;
  return (
    <p role={message.tone === "problem" ? "alert" : undefined} className={`mic-message ${message.tone}`}>
      <span>{message.text}</span>
      <button type="button" className="link small" onClick={onDismiss}>Dismiss</button>
    </p>
  );
}

/** The answer box's end of it: the message to show, and dismissing it. */
export function useMicMessage(): [MicMessageT | null, (m: MicMessageT | null) => void, () => void] {
  const [message, setMessage] = useState<MicMessageT | null>(null);
  return [message, setMessage, () => setMessage(null)];
}

export function MicButton({ voice, onText, disabled, onMessage }: { voice: IntakeVoice; onText: (text: string) => void; disabled?: boolean; onMessage?: (m: MicMessageT | null) => void }) {
  const rec = useRecorder(voice.transcribe, onText);
  const [firstUse, markUsed, hideFirstUse] = useFirstUse();
  const message: MicMessageT | null = rec.problem ? { text: problemText(rec.problem, rec.device), tone: "problem" } : firstUse ? { text: AUDIO_NOT_KEPT, tone: "note" } : null;
  const messageKey = message ? `${message.tone}:${message.text}` : "";
  useEffect(() => { onMessage?.(message); }, [messageKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  // a message is read and then gets out of the way; a new recording clears it straight away
  useEffect(() => {
    if (!messageKey) return;
    const t = setTimeout(() => { rec.clearProblem(); hideFirstUse(); }, MESSAGE_MS);
    return () => clearTimeout(t);
  }, [messageKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  // dictation works whether or not questions are read aloud: the Voice toggle is only about the agent speaking
  if (!voice.available) return null;
  const recording = rec.state === "recording";
  const label = recording ? "Stop recording" : rec.state === "transcribing" ? "Transcribing…" : "Dictate your answer";
  return (
    <span className="mic">
      <button type="button" className={`mic-btn${recording ? " recording" : ""}`} aria-pressed={recording} aria-label={label} title={`${label}. ${AUDIO_NOT_KEPT}`}
        disabled={disabled || rec.state === "transcribing"} onClick={() => { voice.interrupt(); voice.warm(); markUsed(); rec.toggle(); }}>
        <MicIcon />
        {recording && <span className="level" aria-hidden style={{ transform: `scaleX(${0.15 + rec.level * 0.85})` }} />}
      </button>
      {(recording || rec.state === "transcribing") && <span role="status" className="muted small">{recording ? `Listening${rec.device ? ` on ${rec.device}` : ""}… press again to stop` : "Transcribing…"}</span>}
      {!onMessage && message && <span role={message.tone === "problem" ? "alert" : undefined} className={`mic-message ${message.tone}`}>{message.text}</span>}
    </span>
  );
}

/** The recorder's message, plus which microphone was listening when nothing usable came from it. */
export function problemText(problem: RecorderProblem, device: string | null): string {
  if (problem !== "quiet" && problem !== "unclear") return PROBLEM_TEXT[problem];
  if (device && /iphone|ipad/i.test(device)) return `${PROBLEM_TEXT[problem]} It was listening on ${device}: a phone nearby has taken over as this computer's microphone. Speak into the phone, or pick the built-in microphone under System Settings › Sound › Input.`;
  return `${PROBLEM_TEXT[problem]}${device ? ` It was listening on ${device}.` : ""}`;
}

/** What the transcription heard, shown greyed under the answer so a misheard word is easy to spot. */
export function Heard({ text }: { text: string | null }) {
  return text ? <p className="heard small" aria-label="What was heard">Heard: &ldquo;{text}&rdquo;</p> : null;
}

/** Inserts dictated words at the cursor, or appends them when the cursor is not in the box. */
export function insertAt(current: string, words: string, el: HTMLTextAreaElement | HTMLInputElement | null): string {
  const at = el && document.activeElement === el && el.selectionStart !== null ? el.selectionStart : current.length;
  const before = current.slice(0, at);
  const after = current.slice(at);
  const lead = before && !/\s$/.test(before) ? " " : "";
  const trail = after && !/^\s/.test(after) ? " " : "";
  return `${before}${lead}${words}${trail}${after}`;
}

function MicIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><path d="M12 17v5" /></svg>;
}
